import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Card, CardHeader } from '@/components/atoms/Card';
import Field from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextField from '@/components/atoms/TextField';
import { translateDataLabel } from '@/i18n/dataLabels';

import type { SettingsFormProps } from './settingsFormState';
import { useCageSettingsForm } from './useCageSettingsForm';

/** The cage's own settings card. The page lays it out beside the fish-group card. */
export function SettingsForm(props: SettingsFormProps) {
    const { t } = useTranslation('cageSettings');
    const { t: tLocations } = useTranslation('clientLocations');
    const { t: tCommon } = useTranslation('common');
    // TODO: cage type names come from the DB as snake_case strings and are used as translation keys.
    // This is legacy behaviour — decoupling requires changes to CageType interfaces and mappers.
    const { t: tLegacy } = useTranslation();

    const cageNameId = useId();
    const cageTypeSelectId = useId();
    const fishTypeSelectId = useId();
    const externalNameId = useId();
    const externalIdFieldId = useId();

    const {
        formState,
        status,
        visibleCageTypes,
        fishTypesList,
        isLocked,
        handleNameChange,
        handleCageTypeChange,
        handleFishTypeChange,
        handleExternalNameChange,
        handleExternalIdChange,
        handleSubmit,
    } = useCageSettingsForm(props);

    return (
        <Card
            as="section"
            padding="xl"
            header={<CardHeader title={t('settings.boxTitle')} level="h2" />}>
            <form>
                <Field label={<>{t('settings.cageName')}:</>} htmlFor={cageNameId}>
                    <TextField
                        id={cageNameId}
                        type="text"
                        name="name"
                        placeholder={formState.name.value}
                        value={formState.name.value}
                        onChange={handleNameChange}
                        invalid={!!status.error}
                    />
                </Field>
                <Field
                    label={<>{tLocations('addCageModal.cageType')}:</>}
                    htmlFor={cageTypeSelectId}>
                    <Select
                        id={cageTypeSelectId}
                        name="cagetype"
                        value={formState.cageType.value ?? -1}
                        onChange={handleCageTypeChange}
                        disabled={isLocked}
                        invalid={!!status.error}>
                        {!isLocked && (
                            <option
                                value={
                                    -1
                                }>{`--- ${tLocations('addCageModal.chooseCageType')} ---`}</option>
                        )}
                        {visibleCageTypes.map((cageType) => (
                            <option key={cageType.id} value={cageType.id}>
                                {translateDataLabel(tLegacy, 'cageTypes', cageType.name)}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field
                    label={<>{tLocations('addCageModal.fishType')}:</>}
                    htmlFor={fishTypeSelectId}>
                    <Select
                        id={fishTypeSelectId}
                        name="fishtype"
                        value={formState.fishType.value ?? -1}
                        onChange={handleFishTypeChange}
                        invalid={!!status.error}>
                        <option
                            value={
                                -1
                            }>{`--- ${tLocations('addCageModal.chooseFishType')} ---`}</option>
                        {fishTypesList.map((fishType) => (
                            <option key={fishType.id} value={fishType.id}>
                                {translateDataLabel(tLegacy, 'fishTypes', fishType.name)}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field
                    label={<>{tLocations('addCageModal.externalName')}:</>}
                    htmlFor={externalNameId}>
                    <TextField
                        id={externalNameId}
                        type="text"
                        name="externalName"
                        placeholder={formState.externalName.value}
                        value={formState.externalName.value}
                        onChange={handleExternalNameChange}
                        invalid={!!status.error}
                    />
                </Field>
                <Field
                    label={<>{tLocations('addCageModal.externalId')}:</>}
                    htmlFor={externalIdFieldId}>
                    <TextField
                        id={externalIdFieldId}
                        type="text"
                        name="externalId"
                        placeholder={formState.externalId.value ?? ''}
                        value={formState.externalId.value ?? ''}
                        onChange={handleExternalIdChange}
                        invalid={!!status.error}
                    />
                </Field>
                {status.error && (
                    <Alert tone="danger" size="small">
                        {status.error}
                    </Alert>
                )}
                <Button
                    disabled={status.isSubmitting}
                    trackingId="cage_settings_save"
                    onClick={handleSubmit}>
                    {tCommon('actions.save')}
                </Button>
            </form>
            {status.successMessage && <span>{status.successMessage}</span>}
        </Card>
    );
}
